import { useState } from 'react';
import '@/App.css';
import axios from 'axios';
import { Menu, X, Ship, Droplet, Wheat, Globe2, Phone, Mail, MapPin, Printer, CheckCircle2, TrendingUp, Shield, Users, Linkedin, Facebook } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product_interest: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setFormStatus({ 
        type: 'success', 
        message: 'Thank you for your inquiry! We will contact you shortly.' 
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        product_interest: '',
        message: ''
      });
    } catch (error) {
      setFormStatus({ 
        type: 'error', 
        message: 'Failed to submit inquiry. Please try again or contact us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img src="/tyrian-logo.png" alt="Tyrian Shipping Limited" className="h-14 w-14 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-blue-900">Tyrian Shipping Limited</h1>
                <p className="text-xs text-gray-600">International Commodities Trading</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-900 font-medium transition">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-900 font-medium transition">About</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-900 font-medium transition">Products</button>
              <button onClick={() => scrollToSection('global')} className="text-gray-700 hover:text-blue-900 font-medium transition">Global Reach</button>
              <button onClick={() => scrollToSection('contact')} className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition">Contact Us</button>
            </div>

            {/* Mobile menu button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-gray-700 hover:text-blue-900">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-gray-700 hover:text-blue-900">About</button>
              <button onClick={() => scrollToSection('products')} className="block w-full text-left py-2 text-gray-700 hover:text-blue-900">Products</button>
              <button onClick={() => scrollToSection('global')} className="block w-full text-left py-2 text-gray-700 hover:text-blue-900">Global Reach</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 bg-blue-900 text-white rounded-lg px-4">Contact Us</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1583857671904-a716bf4ee5d8?w=1920&q=80" 
            alt="Global Shipping" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="hero-title">Global Commodity Trading Excellence</h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-100">Your trusted partner in international petroleum, agricultural, and commodity trading</p>
          <button 
            onClick={() => scrollToSection('contact')} 
            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105"
            data-testid="hero-cta-button"
          >
            Request a Quote
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4" data-testid="about-title">About Tyrian Shipping Limited</h2>
            <div className="w-24 h-1 bg-blue-900 mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">International Commodities Trading</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Tyrian Shipping Limited is a premier international commodities trading company, acting as a trusted broker and intermediary between sellers and buyers worldwide. We physically source commodities and products from our global supplier base to deliver to our customers across the globe.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Our expertise spans petroleum products, agricultural commodities, precious metals, and industrial materials. We pride ourselves on building strong, long-lasting relationships with both suppliers and customers.
              </p>
              <h4 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Our Services</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">International Commodities Trading & Brokerage</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">Global Sourcing & Supply Chain Management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-600">Consulting & Digital Marketing Services</span>
                </li>
              </ul>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <TrendingUp className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-800 mb-2">Global Network</h4>
                <p className="text-gray-600 text-sm">Worldwide supplier and customer base</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <Shield className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-800 mb-2">Trusted Broker</h4>
                <p className="text-gray-600 text-sm">Reliable intermediary services</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <Users className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-800 mb-2">Expert Team</h4>
                <p className="text-gray-600 text-sm">Industry professionals</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg text-center">
                <Globe2 className="h-12 w-12 text-blue-900 mx-auto mb-4" />
                <h4 className="font-semibold text-gray-800 mb-2">24/7 Support</h4>
                <p className="text-gray-600 text-sm">Always available for clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4" data-testid="products-title">Our Products</h2>
            <div className="w-24 h-1 bg-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">We specialize in sourcing and trading a diverse range of commodities to meet global demand</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Petroleum Products */}
            <div className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition" data-testid="product-petroleum">
              <div className="relative h-64">
                <img 
                  src="https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?w=800&q=80" 
                  alt="Petroleum Products" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <Droplet className="h-10 w-10 text-white mb-2" />
                  <h3 className="text-2xl font-bold text-white">Petroleum Products</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Crude Oil</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Diesel (EN 590 & ULSD)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Gasoline & Jet Fuels (all grades)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Natural Gas, LPG & LNG</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Mazut & Bitumen</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Commodities */}
            <div className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition" data-testid="product-commodities">
              <div className="relative h-64">
                <img 
                  src="https://images.unsplash.com/photo-1594571603435-0b192ab6dafa?w=800&q=80" 
                  alt="Commodities" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <Globe2 className="h-10 w-10 text-white mb-2" />
                  <h3 className="text-2xl font-bold text-white">Global Commodities</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Urea & Fertilizers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Copper & Precious Metals</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Gold & Diamonds (rough & polished)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Cement & Construction Materials</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Foreign Exchange & Investments</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Agricultural Products */}
            <div className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition" data-testid="product-agriculture">
              <div className="relative h-64">
                <img 
                  src="https://images.unsplash.com/photo-1555064837-3c7ae70f81be?w=800&q=80" 
                  alt="Agricultural Products" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <Wheat className="h-10 w-10 text-white mb-2" />
                  <h3 className="text-2xl font-bold text-white">Agricultural Products</h3>
                </div>
              </div>
              <div className="p-6">
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Sugar & Sweeteners</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Wheat & Corn</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Soya Beans & Animal Feed</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Edible Oils</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-900 mr-2">•</span>
                    <span>Alfalfa & Grains</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section id="global" className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" data-testid="global-title">Global Presence</h2>
            <div className="w-24 h-1 bg-white mx-auto mb-4"></div>
            <p className="text-gray-200 max-w-2xl mx-auto">Operating worldwide with a network of trusted suppliers and satisfied customers across continents</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <Globe2 className="h-16 w-16 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-2">Global</h3>
              <p className="text-gray-300">Worldwide Operations</p>
            </div>
            <div>
              <Ship className="h-16 w-16 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-2">Reliable</h3>
              <p className="text-gray-300">Logistics Network</p>
            </div>
            <div>
              <Users className="h-16 w-16 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-2">Expert</h3>
              <p className="text-gray-300">Professional Team</p>
            </div>
            <div>
              <Shield className="h-16 w-16 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-2">Trusted</h3>
              <p className="text-gray-300">By Industry Leaders</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4" data-testid="contact-title">Get In Touch</h2>
            <div className="w-24 h-1 bg-blue-900 mx-auto mb-4"></div>
            <p className="text-gray-600 max-w-2xl mx-auto">Ready to discuss your commodity trading needs? Contact us today for a quote</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-blue-900 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Address</h4>
                    <p className="text-gray-600">Zokak Al Balat, Shartouni Street</p>
                    <p className="text-gray-600">Abed Al Fatah Building</p>
                    <p className="text-gray-600">Beirut, Lebanon</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 text-blue-900 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Phone</h4>
                    <p className="text-gray-600">+961 76388467</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Printer className="h-6 w-6 text-blue-900 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Fax</h4>
                    <p className="text-gray-600">+961 1377443</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 text-blue-900 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-1">Email</h4>
                    <p className="text-gray-600">info@tyrianshippingltd.com</p>
                    <p className="text-gray-600 text-sm mt-1">abbas224@hotmail.com</p>
                    <p className="text-gray-600 text-sm">mhd.s.abbas@outlook.com</p>
                    <p className="text-gray-600 text-sm">mhd.s.abbas@protonmail.com</p>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg mt-8">
                  <h4 className="font-semibold text-gray-800 mb-2">General Manager</h4>
                  <p className="text-gray-700 font-medium mb-4">Mohamed Abbas</p>
                  <div className="flex space-x-4">
                    <a 
                      href="https://www.linkedin.com/in/mohamed-abbas-1827914/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-blue-900 hover:text-blue-700 transition"
                      data-testid="linkedin-link"
                    >
                      <Linkedin className="h-5 w-5 mr-2" />
                      <span className="text-sm">LinkedIn</span>
                    </a>
                    <a 
                      href="https://www.facebook.com/mohamed.abbas.5876" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-blue-900 hover:text-blue-700 transition"
                      data-testid="facebook-link"
                    >
                      <Facebook className="h-5 w-5 mr-2" />
                      <span className="text-sm">Facebook</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Request a Quote</h3>
              
              {formStatus.message && (
                <div className={`mb-6 p-4 rounded-lg ${
                  formStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`} data-testid="form-status-message">
                  {formStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2" htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2" htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    data-testid="contact-form-email"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2" htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2" htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    data-testid="contact-form-company"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2" htmlFor="product_interest">Product Interest</label>
                  <select
                    id="product_interest"
                    name="product_interest"
                    value={formData.product_interest}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    data-testid="contact-form-product-interest"
                  >
                    <option value="">Select a product category</option>
                    <option value="Petroleum Products">Petroleum Products</option>
                    <option value="Crude Oil">Crude Oil</option>
                    <option value="Agricultural Products">Agricultural Products</option>
                    <option value="Commodities">Commodities & Metals</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2" htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent"
                    data-testid="contact-form-message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-900 text-white py-3 rounded-lg font-semibold hover:bg-blue-800 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img src="/tyrian-logo.png" alt="Tyrian Shipping Limited" className="h-12 w-12 object-contain" />
                <div>
                  <h3 className="text-xl font-bold">Tyrian Shipping Limited</h3>
                  <p className="text-sm text-gray-300">International Commodities Trading</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">
                Your trusted partner in global commodity trading and supply chain solutions.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition">About Us</button></li>
                <li><button onClick={() => scrollToSection('products')} className="hover:text-white transition">Products</button></li>
                <li><button onClick={() => scrollToSection('global')} className="hover:text-white transition">Global Reach</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2 text-gray-300 text-sm mb-4">
                <li>+961 76388467</li>
                <li>info@tyrianshippingltd.com</li>
                <li>Beirut, Lebanon</li>
              </ul>
              <div className="flex space-x-4 mt-4">
                <a 
                  href="https://www.linkedin.com/in/mohamed-abbas-1827914/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-6 w-6" />
                </a>
                <a 
                  href="https://www.facebook.com/mohamed.abbas.5876" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition"
                  aria-label="Facebook"
                >
                  <Facebook className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-blue-800 mt-8 pt-8 text-center text-gray-300 text-sm">
            <p>&copy; {new Date().getFullYear()} Tyrian Shipping Limited. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;